import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users,
  Megaphone,
  Layers,
  RefreshCw,
  Loader2,
  Search,
  ExternalLink,
  X,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  MessageSquare,
} from "lucide-react";
import api from "../lib/api";
import { crmUrl, crmBaseUrl } from "../lib/crmUrl";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

// =============================================================================
// Sales → Leads & Pipeline
// =============================================================================
// A mirror of the partner CRM's pipeline, readable without opening
// crm.smartorange.space.
//
// Three rules this page is built on, each of them a fix for something that was
// wrong before:
//
//  1. ONE filter bar governs the whole page. Filters used to be scattered
//     (period buttons here, stage tiles there) and the stage tiles secretly
//     filtered only the list at the bottom.
//  2. Filtering is cross-filtering: a filter narrows every block EXCEPT the one
//     it was chosen in, so choosing a manager never erases the manager list.
//  3. Leads live INSIDE the row they belong to. Clicking a campaign or a
//     manager opens that row's leads, instead of a detached table at the
//     bottom that never said whose leads it was showing.
//
// Marker: sales-leads-board-v2
// =============================================================================

const UNTAGGED = "__untagged__";
const NO_ADS = "__no_ads__";
const ARCHIVE_OWNER = "__archive__";

const PRESETS = [
  { key: "30d", label: "30 дней", days: 30 },
  { key: "90d", label: "90 дней", days: 90 },
  { key: "365d", label: "Год", days: 365 },
  { key: "all", label: "Всё время", days: null },
];

const OBJECTIVE_LABELS = {
  OUTCOME_LEADS: "Лиды",
  OUTCOME_AWARENESS: "Охват",
  OUTCOME_TRAFFIC: "Трафик",
  OUTCOME_SALES: "Продажи",
  OUTCOME_ENGAGEMENT: "Вовлечение",
  OUTCOME_APP_PROMOTION: "Приложение",
};

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function campaignLabel(row) {
  if (!row) return "";
  if (row.kind === "untagged" || row.key === UNTAGGED) return "Наша реклама без метки";
  if (row.kind === "manual" || row.key === NO_ADS) return "Заведено вручную";
  return row.label || row.key;
}

function money(byCurrency) {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => v);
  if (!entries.length) return null;
  // Per currency on purpose: the deals carry EUR, USD and UAH and one merged
  // number would be dishonest.
  return entries
    .map(([ccy, v]) => `${Math.round(v).toLocaleString("ru-RU")} ${ccy}`)
    .join(" · ");
}

function usd(v, ccy = "USD") {
  if (v === null || v === undefined) return null;
  return `${Math.round(v).toLocaleString("ru-RU")} ${ccy === "USD" ? "$" : ccy}`;
}

function peoplePlural(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "человек";
  if (mod10 === 1) return "человек";
  if (mod10 >= 2 && mod10 <= 4) return "человека";
  return "человек";
}

function stageTone(kind) {
  if (kind === "won") return "text-emerald-500";
  if (kind === "lost") return "text-rose-500";
  return "text-sky-500";
}

/** A compact labelled picker. Native <select> on purpose: it is denser than a
 *  popup listbox, keyboard- and screen-reader-correct for free, and this bar
 *  carries six controls plus a search field. */
function FilterSelect({ label, value, options, onChange, allLabel }) {
  const active = !!value;
  return (
    <label
      className={`relative inline-flex items-center h-8 rounded-md border pl-2.5 pr-1 text-xs
                  shrink-0 transition ${
        active ? "border-indigo-500 text-indigo-500" : "border-border text-muted-foreground"
      }`}
    >
      <span className="mr-1.5 whitespace-nowrap">{label}:</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className={`bg-transparent outline-none cursor-pointer max-w-[170px] py-1 pr-1 ${
          active ? "text-indigo-500 font-medium" : "text-foreground"
        }`}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <button
      onClick={onClear}
      className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 text-indigo-500 px-2.5 py-1 text-xs hover:bg-indigo-500/20"
    >
      {label}
      <X className="h-3 w-3" />
    </button>
  );
}

function Kpi({ label, value, hint, tone, onClick, active }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition ${
        active ? "border-indigo-500 bg-indigo-500/10" : "border-border bg-card"
      } ${onClick ? "hover:border-indigo-500/60 cursor-pointer" : ""}`}
    >
      <div className={`text-2xl font-semibold tabular-nums ${tone || ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      {hint ? <div className="text-[11px] text-muted-foreground/70 mt-0.5">{hint}</div> : null}
    </Tag>
  );
}

/** The pipeline in one strip instead of a wall of equal squares.
 *
 *  Open stages share a proportional row, so "7 on Meeting booked" reads as
 *  wider than "1 on First contact" without counting. Closed deals sit apart:
 *  with 210 lost against 22 open, one shared bar would be 90% red and say
 *  nothing about the pipeline.
 */
function FunnelStrip({ stages, active, onPick, loading, narrowed }) {
  const open = stages.filter((s) => s.kind === "open");
  const won = stages.find((s) => s.kind === "won");
  const lost = stages.find((s) => s.kind === "lost");
  const openTotal = open.reduce((a, s) => a + s.count, 0);
  const closedTotal = (won?.count || 0) + (lost?.count || 0);

  return (
    <Card>
      <CardHeader className="pb-2.5">
        <CardTitle className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
          <Layers className="h-4 w-4" /> Воронка
          <span className="text-xs font-normal text-muted-foreground">
            {openTotal} в работе · {closedTotal} закрыто
          </span>
          {narrowed ? (
            <span className="text-[11px] font-normal text-muted-foreground/70">· в рамках фильтров</span>
          ) : null}
          <span className="ml-auto text-[11px] font-normal text-muted-foreground/60">
            клик по стадии фильтрует страницу
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {open.length ? (
          <div className="flex gap-1 items-stretch">
            {open.map((s) => {
              const isActive = active === s.stage_id;
              return (
                <button
                  key={s.stage_id}
                  onClick={() => onPick(s.stage_id)}
                  style={{ flexGrow: Math.max(s.count, 1) }}
                  title={`${s.label} — ${s.count}`}
                  className={`min-w-[74px] basis-0 rounded-md border px-2.5 py-2 text-left transition ${
                    isActive ? "border-indigo-500 bg-indigo-500/10"
                             : "border-border hover:border-indigo-500/50"
                  }`}
                >
                  <div className="text-base font-semibold tabular-nums leading-none text-sky-500">
                    {s.count}
                  </div>
                  <div className="mt-1 text-[11px] leading-tight text-muted-foreground truncate">
                    {s.label}
                  </div>
                </button>
              );
            })}
          </div>
        ) : !loading ? (
          <div className="text-sm text-muted-foreground">Открытых сделок нет.</div>
        ) : null}

        {closedTotal ? (
          <div className="flex gap-1.5 pt-0.5">
            {[won, lost].filter(Boolean).map((s) => {
              const isActive = active === s.stage_id;
              const isWon = s.kind === "won";
              return (
                <button
                  key={s.stage_id}
                  onClick={() => onPick(s.stage_id)}
                  className={`flex items-baseline gap-2 rounded-md border px-3 py-1.5 transition ${
                    isActive ? "border-indigo-500 bg-indigo-500/10"
                      : `border-border hover:border-indigo-500/50 ${
                          isWon ? "bg-emerald-500/5" : "bg-rose-500/5"}`
                  }`}
                >
                  <span className={`text-sm font-semibold tabular-nums ${
                    isWon ? "text-emerald-500" : "text-rose-500"}`}>
                    {s.count}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{s.label}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Where a row's deals sit, as one bar instead of thirteen columns. */
function StageMix({ row, stages }) {
  const total = row.total || 1;
  const parts = stages
    .map((s) => ({ ...s, n: row.stages?.[s.id] || 0 }))
    .filter((s) => s.n);
  if (!parts.length) return null;
  return (
    <div className="flex h-1.5 rounded-sm overflow-hidden bg-muted/50 w-full max-w-[220px]">
      {parts.map((s) => (
        <span
          key={s.id}
          title={`${s.label}: ${s.n}`}
          style={{ width: `${(s.n / total) * 100}%` }}
          className={
            s.kind === "won" ? "bg-emerald-500"
              : s.kind === "lost" ? "bg-rose-500/70"
              : "bg-sky-500"
          }
        />
      ))}
    </div>
  );
}

/** The leads of one row, opened inside that row.
 *
 *  This replaces the old detached table at the bottom of the page: a list of
 *  leads is only meaningful next to the thing it belongs to.
 */
function LeadsDrawer({ params, colSpan }) {
  const [state, setState] = useState({ items: [], total: 0, loading: true });
  const [limit, setLimit] = useState(15);
  const key = JSON.stringify(params);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    api
      .get("/sales-crm/deals", { params: { ...params, limit, offset: 0 } })
      .then(({ data }) => alive && setState({ items: data.items, total: data.total, loading: false }))
      .catch(() => alive && setState({ items: [], total: 0, loading: false }));
    return () => { alive = false; };
  }, [key, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <tr className="bg-muted/20">
      <td colSpan={colSpan} className="px-4 py-3">
        {state.loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Загружаю лиды…
          </div>
        ) : !state.items.length ? (
          <div className="text-xs text-muted-foreground py-2">Лидов нет.</div>
        ) : (
          <>
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-xs">
                <tbody>
                  {state.items.map((d) => (
                    <tr key={d.id} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-1.5 max-w-[300px]">
                        <div className="truncate font-medium" title={d.name || ""}>
                          {d.lead_type ? (
                            <span className={`mr-1.5 rounded px-1 py-0.5 text-[9px] font-semibold uppercase ${
                              d.lead_type === "CHAT_LEAD" ? "bg-violet-500/15 text-violet-500"
                                : d.lead_type === "BOOKING_LEAD" ? "bg-amber-500/15 text-amber-500"
                                : "bg-sky-500/15 text-sky-500"}`}>
                              {d.lead_type === "CHAT_LEAD" ? "чат"
                                : d.lead_type === "BOOKING_LEAD" ? "бронь" : "форма"}
                            </span>
                          ) : null}
                          {d.name || "Без названия"}
                        </div>
                        {d.company_name ? (
                          <div className="text-muted-foreground truncate">{d.company_name}</div>
                        ) : null}
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap ${stageTone(d.stage_kind)}`}>
                        {d.stage_label}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {d.qualified === "Yes" ? <span className="text-emerald-500">наш</span>
                          : d.qualified === "No" ? <span className="text-rose-500">не наш</span>
                          : <span className="text-muted-foreground/40">не оценён</span>}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">
                        {d.owner_name}
                      </td>
                      <td className="px-3 py-1.5 max-w-[190px] truncate text-muted-foreground"
                          title={d.campaign_kind === "ads" ? d.campaign : ""}>
                        {d.campaign_kind === "ads" ? d.campaign
                          : d.campaign_kind === "untagged" ? "без метки" : "вручную"}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">
                        {[d.city, d.country].filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right tabular-nums">
                        {d.amount ? `${Math.round(d.amount).toLocaleString("ru-RU")} ${d.currency || ""}` : ""}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">
                        {d.created_at ? new Date(d.created_at).toLocaleDateString("ru-RU") : ""}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <a href={crmUrl("deal", d.id) || crmBaseUrl} target="_blank" rel="noopener noreferrer"
                           className="text-muted-foreground hover:text-indigo-500 inline-flex" title="Открыть в CRM">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {state.total > state.items.length ? (
              <button onClick={() => setLimit((l) => l + 50)}
                      className="mt-2 text-xs text-indigo-500 hover:underline">
                Показать ещё — всего {state.total}
              </button>
            ) : null}
          </>
        )}
      </td>
    </tr>
  );
}

export default function SalesLeadsPage() {
  const [preset, setPreset] = useState("90d");
  const [board, setBoard] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(true);
  const [archiveExpanded, setArchiveExpanded] = useState(false);

  const [ownerFilter, setOwnerFilter] = useState(null);
  const [stageFilter, setStageFilter] = useState(null);
  const [campaignFilter, setCampaignFilter] = useState(null);
  const [platformFilter, setPlatformFilter] = useState(null);
  const [objectiveFilter, setObjectiveFilter] = useState(null);
  const [source, setSource] = useState("all");
  // The CRM's own answer: "Yes" | "No" | "none" (not judged yet).
  const [qualifiedFilter, setQualifiedFilter] = useState(null);
  // FORM_LEAD | CHAT_LEAD | BOOKING_LEAD | "none" (came in some other way).
  const [leadTypeFilter, setLeadTypeFilter] = useState(null);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");

  const [openOwner, setOpenOwner] = useState(null);
  const [openCampaign, setOpenCampaign] = useState(null);

  const range = useMemo(() => {
    const p = PRESETS.find((x) => x.key === preset);
    if (!p || !p.days) return {};
    return { date_from: isoDaysAgo(p.days), date_to: new Date().toISOString().slice(0, 10) };
  }, [preset]);

  const pageFilters = useMemo(() => ({
    ...range,
    source: source === "all" ? undefined : source,
    owner_id: ownerFilter || undefined,
    stage_id: stageFilter || undefined,
    campaign: campaignFilter || undefined,
    platform: platformFilter || undefined,
    objective: objectiveFilter || undefined,
    qualified: qualifiedFilter || undefined,
    lead_type: leadTypeFilter || undefined,
    q: search || undefined,
  }), [range, source, ownerFilter, stageFilter, campaignFilter, platformFilter,
      objectiveFilter, qualifiedFilter, leadTypeFilter, search]);

  const loadBoard = useCallback(async () => {
    setLoadingBoard(true);
    try {
      const { data } = await api.get("/sales-crm/board", {
        params: { ...pageFilters, archive: archiveExpanded ? "expand" : "collapse" },
      });
      setBoard(data);
    } finally {
      setLoadingBoard(false);
    }
  }, [pageFilters, archiveExpanded]);

  useEffect(() => { loadBoard(); }, [loadBoard]);
  // A filter change invalidates whatever row was open — its leads were a
  // different set.
  useEffect(() => { setOpenOwner(null); setOpenCampaign(null); }, [pageFilters]);

  const stages = useMemo(() => {
    if (!board) return [];
    const live = new Set((board.by_stage || []).filter((s) => s.count).map((s) => s.stage_id));
    return (board.stage_order || []).filter((s) => live.has(s.id));
  }, [board]);

  const t = board?.totals;
  const anyFilter = ownerFilter || stageFilter || campaignFilter || platformFilter
    || objectiveFilter || qualifiedFilter || leadTypeFilter || search || source !== "all";

  const clearFilters = () => {
    setOwnerFilter(null); setStageFilter(null); setCampaignFilter(null);
    setPlatformFilter(null); setObjectiveFilter(null); setQualifiedFilter(null);
    setLeadTypeFilter(null);
    setSearch(""); setQ(""); setSource("all");
  };

  const ownerOptions = useMemo(
    () => (board?.by_owner || []).map((o) => ({ value: o.owner_id, label: `${o.owner_name} (${o.total})` })),
    [board]);
  const stageOptions = useMemo(
    () => (board?.by_stage || []).map((s) => ({ value: s.stage_id, label: `${s.label} (${s.count})` })),
    [board]);
  const campaignOptions = useMemo(
    () => (board?.by_campaign || []).map((c) => ({ value: c.key, label: `${campaignLabel(c)} (${c.total})` })),
    [board]);
  const platformOptions = useMemo(
    () => (board?.by_platform || []).map((p) => ({ value: p.platform, label: `${p.platform} (${p.total})` })),
    [board]);
  const leadTypeOptions = useMemo(
    () => (board?.by_lead_type || []).map((r) => ({ value: r.value, label: `${r.label} (${r.total})` })),
    [board]);
  const labelOfLeadType = (v) => board?.by_lead_type?.find((r) => r.value === v)?.label || v;
  const qualifiedOptions = useMemo(
    () => (board?.by_qualified || []).map((r) => ({ value: r.value, label: `${r.label} (${r.total})` })),
    [board]);
  const labelOfQualified = (v) =>
    board?.by_qualified?.find((r) => r.value === v)?.label || v;
  const unjudged = (board?.by_qualified || []).find((r) => r.value === "none")?.total || 0;
  const objectiveOptions = useMemo(
    () => (board?.objectives || []).map((o) => ({ value: o, label: OBJECTIVE_LABELS[o] || o })),
    [board]);

  const labelOfOwner = (id) => board?.by_owner?.find((o) => o.owner_id === id)?.owner_name || id;
  const labelOfStage = (id) => board?.by_stage?.find((s) => s.stage_id === id)?.label || id;
  const labelOfCampaign = (k) => campaignLabel(board?.by_campaign?.find((c) => c.key === k) || { key: k });

  const spendUnknown = (board?.by_campaign || []).filter(
    (c) => c.spend_status === "no_connector" && c.total);
  const spendUnmatched = (board?.by_campaign || []).filter(
    (c) => c.spend_status === "unmatched" && c.total);

  return (
    <div className="p-6 pt-0 space-y-4 max-w-[1600px] mx-auto">
      <div className="pt-6 pb-1">
        <h1 className="text-2xl font-semibold">Лиды и воронка</h1>
        <p className="text-sm text-muted-foreground">
          Зеркало CRM: сколько лидов, на каких стадиях, у кого и с какой рекламы.
          {board?.synced_at ? (
            <span className="ml-1">
              Синхронизировано {new Date(board.synced_at).toLocaleString("ru-RU")}.
            </span>
          ) : null}
        </p>
      </div>

      {/* One bar for the whole page. Everything below obeys it. */}
      <div className="sticky top-0 z-30 -mx-6 px-6 py-2.5 bg-background/95 backdrop-blur
                      border-y border-border flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-border overflow-hidden shrink-0">
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => setPreset(p.key)}
              className={`px-2.5 py-1.5 text-xs transition ${
                preset === p.key ? "bg-indigo-500/15 text-indigo-500 font-medium" : "hover:bg-muted/60"
              }`}>
              {p.label}
            </button>
          ))}
        </div>

        <FilterSelect label="Менеджер" value={ownerFilter} options={ownerOptions}
                      onChange={setOwnerFilter} allLabel="Все" />
        <FilterSelect label="Стадия" value={stageFilter} options={stageOptions}
                      onChange={setStageFilter} allLabel="Все" />
        <FilterSelect label="Площадка" value={platformFilter} options={platformOptions}
                      onChange={setPlatformFilter} allLabel="Все" />
        <FilterSelect label="Кампания" value={campaignFilter} options={campaignOptions}
                      onChange={setCampaignFilter} allLabel="Все" />
        {objectiveOptions.length > 1 ? (
          <FilterSelect label="Цель" value={objectiveFilter} options={objectiveOptions}
                        onChange={setObjectiveFilter} allLabel="Любая" />
        ) : null}
        <FilterSelect label="Заявка" value={leadTypeFilter} options={leadTypeOptions}
                      onChange={setLeadTypeFilter} allLabel="Любая" />
        <FilterSelect label="Квалификация" value={qualifiedFilter} options={qualifiedOptions}
                      onChange={setQualifiedFilter} allLabel="Любая" />
        <FilterSelect label="Источник" value={source === "all" ? null : source}
                      onChange={(v) => setSource(v || "all")} allLabel="Любой"
                      options={[
                        { value: "ads", label: `Реклама${t ? ` (${t.ad_leads})` : ""}` },
                        { value: "manual", label: `Вручную${t ? ` (${t.manual})` : ""}` },
                      ]} />

        <form onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }}
              className="flex items-center flex-1 min-w-[180px]">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Имя, компания, кампания…" className="h-8 pl-8 text-sm" />
          </div>
        </form>

        {anyFilter ? (
          <Button variant="ghost" size="sm" className="h-8 shrink-0" onClick={clearFilters}>
            <X className="h-3.5 w-3.5 mr-1" /> Сбросить
          </Button>
        ) : null}
        <Button variant="outline" size="sm" className="h-8 shrink-0"
                onClick={loadBoard} disabled={loadingBoard} title="Обновить">
          {loadingBoard ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>

        {anyFilter ? (
          <div className="basis-full flex flex-wrap items-center gap-1.5 pt-0.5">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Фильтры страницы
            </span>
            {ownerFilter ? <FilterChip label={labelOfOwner(ownerFilter)} onClear={() => setOwnerFilter(null)} /> : null}
            {stageFilter ? <FilterChip label={labelOfStage(stageFilter)} onClear={() => setStageFilter(null)} /> : null}
            {platformFilter ? <FilterChip label={platformFilter} onClear={() => setPlatformFilter(null)} /> : null}
            {campaignFilter ? <FilterChip label={labelOfCampaign(campaignFilter)} onClear={() => setCampaignFilter(null)} /> : null}
            {objectiveFilter ? <FilterChip label={OBJECTIVE_LABELS[objectiveFilter] || objectiveFilter} onClear={() => setObjectiveFilter(null)} /> : null}
            {leadTypeFilter ? <FilterChip label={labelOfLeadType(leadTypeFilter)} onClear={() => setLeadTypeFilter(null)} /> : null}
            {qualifiedFilter ? <FilterChip label={labelOfQualified(qualifiedFilter)} onClear={() => setQualifiedFilter(null)} /> : null}
            {source !== "all" ? <FilterChip label={source === "ads" ? "Реклама" : "Заведено вручную"} onClear={() => setSource("all")} /> : null}
            {search ? <FilterChip label={`«${search}»`} onClear={() => { setSearch(""); setQ(""); }} /> : null}
          </div>
        ) : null}
      </div>

      {t ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <Kpi label="Всего лидов" value={t.total} hint={money(t.amount_by_currency)} />
          <Kpi label="В работе" value={t.open} tone="text-sky-500" />
          <Kpi label="Выиграно" value={t.won} tone="text-emerald-500" hint={money(t.won_amount_by_currency)} />
          <Kpi label="Проиграно" value={t.lost} tone="text-rose-500" />
          <Kpi label="Win rate" value={t.win_rate === null ? "—" : `${t.win_rate}%`} hint="от закрытых" />
          <Kpi label="Расход на рекламу"
               value={t.spend === null || t.spend === undefined ? "—" : usd(t.spend, t.spend_currency)}
               hint={`только ${(t.spend_platforms || []).join(", ") || "—"}`} />
          <Kpi label="Цена лида"
               value={t.cpl ? usd(t.cpl, t.spend_currency) : "—"}
               tone="text-amber-500"
               hint={t.leads_with_known_spend ? `по ${t.leads_with_known_spend} лидам с расходом` : "нет данных о расходе"} />
          <Kpi label="Без квалификации" value={unjudged}
               tone={unjudged ? "text-amber-500" : ""}
               hint="менеджер не ответил «наш / не наш»"
               active={qualifiedFilter === "none"}
               onClick={() => setQualifiedFilter(qualifiedFilter === "none" ? null : "none")} />
        </div>
      ) : null}

      {spendUnknown.length ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
          <span className="text-muted-foreground">
            Расход неизвестен по {spendUnknown.length} кампани{spendUnknown.length === 1 ? "и" : "ям"}
            {" "}({spendUnknown.map((c) => c.platform).filter((v, i, a) => a.indexOf(v) === i).join(", ")}):
            коннектор к этим кабинетам ещё не подключён. В цену лида они не входят.
          </span>
        </div>
      ) : null}

      {spendUnmatched.length ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
          <span className="text-muted-foreground">
            {spendUnmatched.length === 1 ? "Кампания" : "Кампании"}{" "}
            {spendUnmatched.map((c) => campaignLabel(c)).join(", ")}: кабинет подключён,
            но кампания в нём названа иначе, чем метка на лиде — расход к ней не привязался.
            Связать имена можно через <span className="font-mono">/api/sales-crm/campaign-aliases</span>.
          </span>
        </div>
      ) : null}

      {(board?.by_lead_type || []).some((r) => r.value !== "none") ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex flex-wrap items-center gap-x-2">
              <MessageSquare className="h-4 w-4" /> Как оставили заявку
              <span className="ml-auto text-[11px] font-normal text-muted-foreground/60">
                «наш клиент» считается от оценённых, не от всех
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {(board?.by_lead_type || []).map((r) => {
                const isActive = leadTypeFilter === r.value;
                return (
                  <button
                    key={r.value}
                    onClick={() => setLeadTypeFilter(isActive ? null : r.value)}
                    className={`rounded-md border px-3 py-2 text-left transition ${
                      isActive ? "border-indigo-500 bg-indigo-500/10"
                               : "border-border hover:border-indigo-500/50"}`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-semibold tabular-nums">{r.total}</span>
                      <span className="text-xs text-muted-foreground">{r.label}</span>
                    </div>
                    <div className="mt-1 text-[11px] leading-tight">
                      {r.qualified_rate === null || r.qualified_rate === undefined ? (
                        <span className="text-muted-foreground/50">никто не оценён</span>
                      ) : (
                        <>
                          <span className="text-emerald-500 font-medium">{r.qualified_rate}%</span>
                          <span className="text-muted-foreground"> наш клиент · из {r.judged} оценённых</span>
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <FunnelStrip
        stages={board?.by_stage || []}
        active={stageFilter}
        onPick={(id) => setStageFilter(stageFilter === id ? null : id)}
        loading={loadingBoard}
        narrowed={board?.basis?.stage != null && board.basis.stage !== t?.total}
      />

      {/* ---------- managers ---------- */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4" /> Менеджеры
            <span className="text-xs font-normal text-muted-foreground">
              · {(board?.by_owner || []).length}
            </span>
            {archiveExpanded ? (
              <button onClick={() => { setArchiveExpanded(false); setOpenOwner(null); }}
                      className="ml-auto text-[11px] font-normal text-indigo-500 hover:underline">
                свернуть архив
              </button>
            ) : (
              <span className="ml-auto text-[11px] font-normal text-muted-foreground/60">
                клик по строке — его лиды
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-medium px-4 py-2">Менеджер</th>
                  <th className="text-left font-medium px-2 py-2 w-[210px]">По стадиям</th>
                  <th className="text-right font-medium px-2 py-2">Лидов</th>
                  <th className="text-right font-medium px-2 py-2"
                      title="Доля бюджета кампаний, с которых пришли лиды этого менеджера. Это разнесение, а не «сколько он потратил».">
                    Расход
                  </th>
                  <th className="text-right font-medium px-2 py-2">Цена лида</th>
                  <th className="text-right font-medium px-2 py-2">В работе</th>
                  <th className="text-right font-medium px-2 py-2">Выиграно</th>
                  <th className="text-right font-medium px-2 py-2">Проиграно</th>
                  <th className="text-right font-medium px-3 py-2">Win&nbsp;%</th>
                </tr>
              </thead>
              <tbody>
                {(board?.by_owner || []).map((row) => {
                  const isOpen = openOwner === row.owner_id;
                  return (
                    <React.Fragment key={row.owner_id}>
                      <tr
                        className={`border-b border-border/60 cursor-pointer transition ${
                          isOpen ? "bg-indigo-500/5" : "hover:bg-muted/40"}`}
                        onClick={() => setOpenOwner(isOpen ? null : row.owner_id)}
                      >
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1.5">
                            {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-indigo-500" />
                                    : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />}
                            <span className={`font-medium ${row.is_archive ? "text-muted-foreground" : ""}`}>
                              {row.owner_name}
                            </span>
                          </span>
                          {row.owner_id === ARCHIVE_OWNER ? (
                            <>
                              <span className="ml-2 text-[11px] text-muted-foreground">
                                {row.archived_count} {peoplePlural(row.archived_count || 0)}, больше не работают
                              </span>
                              <button onClick={(e) => { e.stopPropagation(); setArchiveExpanded(true); }}
                                      className="ml-2 text-[11px] text-indigo-500 hover:underline">
                                показать
                              </button>
                            </>
                          ) : null}
                          {row.is_archive && row.owner_id !== ARCHIVE_OWNER ? (
                            <span className="ml-2 text-[10px] text-muted-foreground">архив</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2"><StageMix row={row} stages={stages} /></td>
                        <td className="px-2 py-2 text-right font-semibold tabular-nums">{row.total}</td>
                        <td className="px-2 py-2 text-right tabular-nums"
                            title={row.leads_with_spend
                              ? `Разнесено по ${row.leads_with_spend} из ${row.total} лидов — остальные без известного расхода`
                              : "Ни один лид этого менеджера не связан с кампанией, по которой есть расход"}>
                          {row.spend ? usd(row.spend, t?.spend_currency)
                            : <span className="text-muted-foreground/30">—</span>}
                        </td>
                        <td className={`px-2 py-2 text-right tabular-nums font-medium ${
                          row.cpl ? "text-amber-500" : "text-muted-foreground/30"}`}>
                          {row.cpl ? usd(row.cpl, t?.spend_currency) : "—"}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-sky-500">{row.open || "·"}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-emerald-500">{row.won || "·"}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-rose-500">{row.lost || "·"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {row.win_rate === null || row.win_rate === undefined ? "—" : `${row.win_rate}%`}
                        </td>
                      </tr>
                      {isOpen ? (
                        <LeadsDrawer colSpan={9}
                          params={{ ...pageFilters, owner_id: row.owner_id }} />
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {!board?.by_owner?.length && !loadingBoard ? (
                  <tr><td colSpan={9} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    За период нет сделок.
                  </td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ---------- campaigns ---------- */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Megaphone className="h-4 w-4" /> Реклама
            <span className="text-xs font-normal text-muted-foreground">
              · {(board?.by_campaign || []).length}
            </span>
            <span className="ml-auto text-[11px] font-normal text-muted-foreground/60">
              клик по строке — лиды кампании
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-medium px-4 py-2">Кампания</th>
                  <th className="text-left font-medium px-2 py-2 w-[190px]">По стадиям</th>
                  <th className="text-right font-medium px-2 py-2">Лидов</th>
                  <th className="text-right font-medium px-2 py-2">Расход</th>
                  <th className="text-right font-medium px-2 py-2">Цена лида</th>
                  <th className="text-right font-medium px-2 py-2">В работе</th>
                  <th className="text-right font-medium px-2 py-2">Выиграно</th>
                  <th className="text-right font-medium px-3 py-2">Проиграно</th>
                </tr>
              </thead>
              <tbody>
                {(board?.by_campaign || []).map((row) => {
                  const isOpen = openCampaign === row.key;
                  const dead = row.no_leads;
                  return (
                    <React.Fragment key={row.key}>
                      <tr
                        className={`border-b border-border/60 transition ${
                          dead ? "" : "cursor-pointer"} ${
                          isOpen ? "bg-indigo-500/5" : "hover:bg-muted/40"}`}
                        onClick={() => !dead && setOpenCampaign(isOpen ? null : row.key)}
                      >
                        <td className="px-4 py-2 max-w-[330px]">
                          <span className="inline-flex items-center gap-1.5 max-w-full">
                            {dead ? <span className="w-3.5" />
                              : isOpen ? <ChevronDown className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />}
                            <span className={`font-medium truncate ${
                              row.kind !== "ads" ? "text-muted-foreground" : ""}`}
                              title={campaignLabel(row)}>
                              {campaignLabel(row)}
                            </span>
                          </span>
                          <div className="flex items-center gap-1.5 mt-0.5 ml-5">
                            {row.platform ? (
                              <span className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase
                                               bg-indigo-500/10 text-indigo-500">
                                {row.platform}
                              </span>
                            ) : null}
                            {row.objective ? (
                              <span className="text-[10px] text-muted-foreground">
                                {OBJECTIVE_LABELS[row.objective] || row.objective}
                              </span>
                            ) : null}
                            {row.campaign_status ? (
                              <span className={`text-[10px] ${
                                row.campaign_status === "ACTIVE" ? "text-emerald-500" : "text-muted-foreground/60"}`}>
                                {row.campaign_status === "ACTIVE" ? "идёт" : "остановлена"}
                              </span>
                            ) : null}
                            {dead ? (
                              <span className="text-[10px] text-amber-500">потратила, лидов нет</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-2 py-2"><StageMix row={row} stages={stages} /></td>
                        <td className="px-2 py-2 text-right font-semibold tabular-nums">
                          {row.total || <span className="text-muted-foreground/40">0</span>}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {row.spend_status === "known" ? usd(row.spend, row.currency)
                            : row.spend_status === "no_connector"
                              ? <span className="text-muted-foreground/50"
                                      title="Коннектор к рекламному кабинету не подключён">нет данных</span>
                            : row.spend_status === "unmatched"
                              ? <span className="text-amber-500/80"
                                      title="Кабинет подключён, но кампания в нём называется иначе, чем метка на лиде. Свяжите имена — и цена лида посчитается.">
                                  не сматчилось
                                </span>
                              : <span className="text-muted-foreground/30">—</span>}
                        </td>
                        <td className={`px-2 py-2 text-right tabular-nums font-medium ${
                          row.cpl ? "text-amber-500" : "text-muted-foreground/30"}`}>
                          {row.cpl ? usd(row.cpl, row.currency) : "—"}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-sky-500">{row.open || "·"}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-emerald-500">{row.won || "·"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-500">{row.lost || "·"}</td>
                      </tr>
                      {isOpen ? (
                        <LeadsDrawer colSpan={8}
                          params={{ ...pageFilters, campaign: row.key }} />
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {!board?.by_campaign?.length && !loadingBoard ? (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    За период нет сделок.
                  </td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
